// Central API base for the frontend.
// In production (Netlify), set REACT_APP_SERVER_ADDRESS to your Vercel backend URL.
// Example: https://qtech-beta.vercel.app
const envBase = process.env.REACT_APP_SERVER_ADDRESS;

// IMPORTANT: CRA env vars are baked at build time. If Netlify env vars are not set
// (or you didn't redeploy), you'll still get the fallback and the site will fail.
//
// To reduce breakage in production, we also allow an optional runtime override:
//   window.__QTECH_API_BASE__ = "https://your-backend.vercel.app"
// You can set this in `public/index.html` later if needed.
const runtimeBase =
  typeof window !== "undefined" && window.__QTECH_API_BASE__ ? window.__QTECH_API_BASE__ : "";

const LOCAL_API_BASE = "http://localhost:800";
const DEPLOYED_API_BASE = "https://qtech-beta.vercel.app";

const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const pushBase = (list, value) => {
  const normalized = normalizeBase(value);
  if (!normalized || list.includes(normalized)) return;
  list.push(normalized);
};

const buildApiBases = () => {
  const bases = [];
  const host =
    typeof window !== "undefined" && window.location ? window.location.hostname : "";
  const isLocalHost = host === "localhost" || host === "127.0.0.1";

  if (isLocalHost) {
    pushBase(bases, LOCAL_API_BASE);
    pushBase(bases, runtimeBase);
    pushBase(bases, envBase);
    pushBase(bases, DEPLOYED_API_BASE);
  } else {
    pushBase(bases, runtimeBase);
    pushBase(bases, envBase);
  }

  if (host && host !== "localhost" && host !== "127.0.0.1") {
    pushBase(bases, DEPLOYED_API_BASE);
    pushBase(bases, window.location.origin);
  }

  if (!isLocalHost) pushBase(bases, LOCAL_API_BASE);
  return bases;
};

export const API_BASES = buildApiBases();
export const API_BASE = API_BASES[0] || LOCAL_API_BASE;

export async function fetchApiJson(path, init) {
  const cleanedPath = path.startsWith("/") ? path : `/${path}`;
  const errors = [];
  const REQUEST_TIMEOUT_MS = 45000;

  for (const base of API_BASES) {
    const url = `${base}${cleanedPath}`;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(url, { ...init, signal: controller.signal });
      const contentType = response.headers.get("content-type") || "";

      if (!contentType.includes("application/json")) {
        const text = await response.text();
        errors.push(`${url} returned non-JSON: ${text.slice(0, 60)}`);
        continue;
      }

      const json = await response.json();

      if (!response.ok) {
        const message = json?.message || `Request failed with status ${response.status}`;
        errors.push(`${url} responded with ${response.status}: ${message}`);
        continue;
      }

      return { base, response, json };
    } catch (error) {
      errors.push(`${url} failed: ${error?.message || "Unknown error"}`);
    } finally {
      clearTimeout(timeout);
    }
  }

  throw new Error(
    errors[0] ||
      `Could not reach any property API. Checked: ${API_BASES.join(", ")}`
  );
}

// Expose for debugging in production builds.
if (typeof window !== "undefined") {
  window.__QTECH_RESOLVED_API_BASE__ = API_BASE;
  window.__QTECH_API_BASE_CANDIDATES__ = API_BASES;
}
